import {
  createNewProject,
  addNewTask,
  updateLocation,
  fetchAllTasks,
  type AddNewTaskRequest,
} from '../api/schedulingApis';
import {
  findProjectRoot,
  hasRootNode,
} from '../features/schedule-locations/logic/locationManagementUtils';
import { useLocationsTreeStore } from '../stores/locationsTreeStore';
import type { ITaskStore } from '../stores/ITaskStore';
import { getProjectDisplayName } from '../utils/projectDisplay';
import {
  buildInitialRootTaskSetup,
  coerceNewTaskFormValues,
  defaultNewTaskFormValues,
  projectHasUiRootTask,
  sanitizeCreateTaskPayload,
  taskSnapshotFromCreateRequest,
} from './addTaskUtils';

function bindLocationToCreate(
  req: AddNewTaskRequest,
  locationId: string,
  locationName: string,
): AddNewTaskRequest {
  return {
    ...req,
    locationId,
    name: req.name || locationName,
    meta: {
      ...(req.meta ?? {}),
      type: 'location',
      locationId,
    },
  };
}

/**
 * Ensure a location project root exists, then seed an empty schedule with a
 * project-named location-type UI root (persisted immediately).
 *
 * Call only after the planning tasks API returned an empty list for this project.
 * Re-checks the server before creating so a stale empty local store cannot wipe real data.
 */
export async function ensureEmptyGanttRootSchedule(params: {
  projectId: string;
  store: ITaskStore;
  calendarId?: string;
}): Promise<{ seeded: boolean }> {
  const projectId = String(params.projectId ?? '').trim();
  if (!projectId) {
    return { seeded: false };
  }

  // Authoritative guard: never seed if the server already has tasks.
  const existing = await fetchAllTasks({ projectId }).catch((err) => {
    console.warn('[Gantt] Empty-seed precheck fetch failed (aborting seed):', err);
    return null;
  });
  if (!existing || !Array.isArray(existing.data)) {
    return { seeded: false };
  }
  if (existing.data.length > 0) {
    console.warn(
      `[Gantt] Skipping empty seed: server has ${existing.data.length} task(s) for ${projectId}`,
    );
    return { seeded: false };
  }
  if (projectHasUiRootTask(params.store.getTasks())) {
    return { seeded: false };
  }

  const locationsStore = useLocationsTreeStore.getState();
  let tree = await locationsStore.fetchTreeIfNeeded(projectId);

  if (!hasRootNode(tree)) {
    const name = getProjectDisplayName() || 'Project';
    await updateLocation([
      {
        id: crypto.randomUUID(),
        projectId,
        name,
        locationType: 'project',
        locationLevel: 1,
        parentLocationId: null,
        locationGroup: [],
      },
    ]);
    await locationsStore.clearTree(projectId);
    tree = await locationsStore.fetchTreeIfNeeded(projectId);
  }

  const root = findProjectRoot(tree);
  const locationId = root?.location_id != null ? String(root.location_id).trim() : '';
  const locationName =
    String(root?.name ?? '').trim() || getProjectDisplayName().trim() || 'Project';
  if (!locationId) {
    throw new Error('Could not resolve location project root after ensure.');
  }

  params.store.setLocationTree(tree);

  const form = coerceNewTaskFormValues({
    ...defaultNewTaskFormValues(null),
    name: locationName,
    taskType: 'location',
  });

  const setup = buildInitialRootTaskSetup({
    projectId,
    form,
    calendarId: params.calendarId?.trim() || undefined,
    projectTitle: locationName,
  });

  const level0 = bindLocationToCreate(setup.level0, locationId, locationName);
  const level1 = bindLocationToCreate(setup.level1, locationId, locationName);

  await createNewProject(setup.project, { silent: true }).catch((err) => {
    // Project may already exist from a prior empty schedule — continue seeding tasks.
    console.warn('[Gantt] createNewProject during empty seed (continuing):', err);
  });
  await addNewTask(sanitizeCreateTaskPayload({ ...level0, taskName: level0.name }), {
    silent: true,
  });
  await addNewTask(sanitizeCreateTaskPayload({ ...level1, taskName: level1.name }), {
    silent: true,
  });

  const shellSnapshot = taskSnapshotFromCreateRequest(level0, {
    tasks: [],
    parent: null,
  });
  const uiRootSnapshot = taskSnapshotFromCreateRequest(level1, {
    tasks: [shellSnapshot],
    parent: shellSnapshot,
  });
  // Snapshots from create request may still need explicit location display fields.
  shellSnapshot.taskType = 'location';
  shellSnapshot.locationId = locationId;
  shellSnapshot.locationType = locationName;
  uiRootSnapshot.taskType = 'location';
  uiRootSnapshot.locationId = locationId;
  uiRootSnapshot.locationType = locationName;

  params.store.setTasks([shellSnapshot, uiRootSnapshot]);
  params.store.clearSessionChanges();
  await params.store.persist(projectId);

  return { seeded: true };
}
